import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
  FocusEvent,
  DetailedHTMLProps,
  useRef,
  useEffect,
} from 'react';
import clsx from 'clsx';
import delay from 'lodash/delay';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import ToolTip from 'core/components/Tooltip';
import {
  useSearchRemittanceAdviceRemarkCodes,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  RemittanceAdviceRemarkCodeDTO,
} from 'dtos';

type Part = 'root' | 'select' | 'input';

type Props = {
  className?: string;
  id: string;
  value?: string | null;
  label?: string;
  placeholder: string;
  readonly?: boolean;
  error?: boolean;
  onChange: (value: string | null) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  inputRef?: RefObject<HTMLInputElement>;
  required?: boolean;
  classes?: Partial<Record<Part, string>>;
};

type RowProps = DetailedHTMLProps<any, any> & {
  option: RemittanceAdviceRemarkCodeDTO;
  onClick: () => void;
};

function RemarkCodeRow({
  option,
  onClick,
  ...props
}: RowProps) {
  return (
    <div
      {...props}
      tabIndex={-1}
      role="button"
      onClick={onClick}
      onKeyDown={noop}
      className="cursor-pointer p-2 flex font-semibold flex-row items-center hover:text-blue-500 border-b border-b-gray-200"
    >
      {option?.remarkCode}
      <ToolTip
        title={option?.description}
        placement="top"
      >
        <span className="!text-xs ml-2 truncate text-gray-600">
          {' - '}
          {option?.description}
        </span>
      </ToolTip>
    </div>
  );
}

export default function RemittanceAdviceRemarkCodeLookup(props: Props) {
  const {
    className,
    id,
    onChange,
    placeholder,
    value,
    readonly,
    label,
    inputRef,
    required = false,
    error = false,
    onBlur,
    classes,
  } = props;
  const [inputValue, setInputValue] = useState<string>(value ?? '');
  const [searchValue, setSearchValue] = useState<string>(value ?? '');
  const blockRef = useRef<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setInputValue(value ?? '');
    if (!open) {
      setSearchValue(value ?? '');
    }
  }, [value, open]);

  const wrapperClasses = clsx({
    '!gap-0': isEmptyString(label),
    '!gap-1': !isEmptyString(label),
  });
  const containerClasses = clsx('w-full', className, classes?.root);

  const {
    data: _options,
    isFetching: isSearching,
  } = useSearchRemittanceAdviceRemarkCodes(
    searchValue,
    open && searchValue?.length > 0,
  );

  const options = useMemo(() => (isSearching ? [] : _options ?? []), [isSearching, _options]);

  const selectedOption = useMemo(() => (
    isEmptyString(value)
      ? null
      : options.find((option) => option?.remarkCode === value) ?? { remarkCode: value ?? undefined }
  ), [options, value]);
  const detachedSelection = inputValue !== (value ?? '');

  const onClose = () => {
    setOpen(false);
  };

  const handleChange = (
    newValue: RemittanceAdviceRemarkCodeDTO | RemittanceAdviceRemarkCodeDTO[] | null,
  ) => {
    setOpen(false);
    const selectedValue = Array.isArray(newValue) ? null : newValue;
    setInputValue(selectedValue?.remarkCode ?? '');
    setSearchValue(selectedValue?.remarkCode ?? '');
    onChange(selectedValue?.remarkCode ?? null);
  };

  const handleSearchChange = useCallback(debounce((newInputValue: string) => {
    if (open) {
      setSearchValue(newInputValue);
    }
  }, 700), [open]);

  useEffect(() => () => {
    handleSearchChange.cancel();
  }, [handleSearchChange]);

  const noOptionsText = useMemo(() => (
    !isSearching && options?.length === 0 && isEmptyString(inputValue)
      ? 'Start typing to see remark codes'
      : 'No remark codes found'
  ), [options, inputValue, isSearching]);

  return (
    <div className={containerClasses}>
      <Wrapper
        id={id}
        className={wrapperClasses}
        label={label}
        required={required}
        error={error}
        showErrorText={false}
        labelClasses="font-semibold text-xs text-gray-500"
      >
        <AutoCompletedInput<RemittanceAdviceRemarkCodeDTO>
          id={id}
          blurOnSelect
          includeInputInList
          disabled={readonly}
          options={options ?? []}
          onOpen={() => { setOpen(true); }}
          onClose={onClose}
          open={open}
          filterSelectedOptions={false}
          getLabel={(option) => option.remarkCode ?? ''}
          renderOption={(optionProps, option) => (
            <RemarkCodeRow
              {...optionProps}
              key={option.remarkCode}
              option={option}
            />
          )}
          loading={isSearching}
          inputRef={inputRef}
          value={detachedSelection ? null : selectedOption}
          openOnFocus
          className={clsx('w-full', classes?.select)}
          onChange={(newValue) => {
            blockRef.current = true;
            handleChange(newValue as RemittanceAdviceRemarkCodeDTO | null);
            delay(() => { blockRef.current = false; }, 250);
          }}
          filterOptions={(x) => x}
          placeholder={placeholder}
          inputValue={inputValue}
          onInputChange={(event: SyntheticEvent, newInputValue: string, reason: string) => {
            if (reason === 'reset' && !blockRef.current) {
              return;
            }
            setInputValue(newInputValue);
            handleSearchChange(newInputValue);
            if (reason === 'input') {
              onChange(isEmptyString(newInputValue) ? null : newInputValue);
            } else if (reason === 'clear') {
              onChange(null);
            }
          }}
          clearOnBlur={false}
          noOptionsText={noOptionsText}
          loadingText="Searching..."
          isOptionEqualToValue={(a, b) => (
            a?.remarkCode === (b as RemittanceAdviceRemarkCodeDTO)?.remarkCode
          )}
          dropdownClassName={clsx('!overflow-hidden !w-[min(24rem,calc(100vw-1rem))]', {
            '!p-0': options?.length && !isNil(selectedOption) && !isSearching,
          })}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              sx: {
                width: 'max-content !important',
                maxWidth: 'min(24rem, calc(100vw - 1rem))',
              },
            },
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            onBlur?.(e);
            if (notEmptyString(e.target?.value) && !blockRef.current) {
              setInputValue(e.target.value);
              setSearchValue(e.target.value);
              onChange(e.target.value);
            }
          }}
          popupIcon={isSearching ? <Spinner /> : <div />}
          inputProps={{ maxLength: 5 }}
          InputProps={{
            classes: { root: clsx('!pr-2', classes?.input) },
          }}
        />
      </Wrapper>
    </div>
  );
}
